// @flow

import { Component } from '../jsti/core';
import EventNames from '../constants/EventNames';
import Notifications from '../constants/Notifications';

export default class ExpandableContent extends Component {
  #content: HTMLElement | null;
  #innerContent: HTMLElement | null;
  #button: HTMLElement | null;
  #contentHeight: number = 0;
  #expandedContentHeight: number = 0;
  #expanded: boolean = false;
  #showMore: string;
  #showLess: string;

  /**
   * Initiate the component
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);
    this.setUp();

    const clickHandler = this.handleExpand.bind(this);

    if (this.#button) {
      this.#button.addEventListener(EventNames.CLICK, clickHandler);
    }

    this.addListener(Notifications.RESIZE, this.handleResize);

    if (this.#expandedContentHeight > this.#contentHeight) {
      this.showButton();
    }
  }

  /**
   * Setup properties & validation
   */
  setUp(): void {
    this.#content = this.el.querySelector('.expandable__content');
    this.#button = this.el.querySelector('button');

    if (!this.#content || !this.#button) {
      throw new Error('Both the content and a button are required');
    }

    if (this.#button) {
      const button: HTMLElement = this.#button;
      this.#showMore = button.dataset.showMore;
      this.#showLess = button.dataset.showLess;
    }

    if (!this.#showMore || !this.#showLess) {
      throw new Error('Both translations for the button are required');
    }

    if (this.#content) {
      const content: HTMLElement = this.#content;

      this.#innerContent = content.firstElementChild;

      if (!this.#innerContent) {
        throw new Error('A paragraph is required in the review content');
      }

      this.#contentHeight = content.offsetHeight;

      if (this.#innerContent) {
        const innerContent: HTMLElement = this.#innerContent;
        this.#expandedContentHeight = innerContent.offsetHeight;
      }
    }
    this.addListener(Notifications.UPDATE_CONTENT_HEIGHT, this.handleResize);
  }

  /**
   * Executes when the user wants to expand the content
   */
  handleExpand(): void {
    if (this.#button) {
      const button: HTMLElement = this.#button;
      if (!this.#expanded) {
        this.changeContentHeight(this.#expandedContentHeight);
        button.innerText = this.#showLess;
        this.#expanded = true;
      } else {
        this.changeContentHeight(this.#contentHeight);
        button.innerText = this.#showMore;
        this.#expanded = false;
      }
    }
  }

  /**
   * Executes when the viewport is resized
   */
  handleResize(): void {
    if (this.#innerContent) {
      this.#expandedContentHeight = this.#innerContent.offsetHeight;
    }

    if (this.#expanded) {
      this.changeContentHeight(this.#expandedContentHeight);
    }
  }

  /**
   * Show the button
   */
  showButton(): void {
    if (this.#button) {
      this.#button.classList.remove('hidden');
    }
  }

  /**
   * Change the height of the content
   */
  changeContentHeight(height: number): void {
    if (this.#content) {
      this.#content.style = 'max-height:' + height + 'px !important';
    }
  }

  /**
   * Destroy all event listeners
   */
  destroy(): void {
    if (this.#button) {
      this.#button.removeEventListener(EventNames.CLICK, this.handleExpand);
    }
    super.destroy();
  }
}
